/**
 * Labels PT-BR + helpers dos contatos autorizados de um Bond (LGPD ADR-129).
 *
 * Os 20 escopos vêm do enum canônico `BOND_AUTHORIZATION_SCOPES` (@lume/shared)
 * — importados, não redefinidos. SCOPE_GROUPS apenas organiza os mesmos códigos
 * em seções para o formulário; uma verificação de exaustividade garante que
 * todo escopo do enum tenha rótulo e pertença a algum grupo.
 */

import { BOND_AUTHORIZATION_SCOPES, type BondAuthorizationScope } from '@lume/shared';

export type AuthorizedContactStatus = 'active' | 'revoked' | 'expired';

export const SCOPE_LABELS: Record<BondAuthorizationScope, string> = {
  // Comum
  financial: 'Financeiro',
  'technical-support': 'Suporte técnico',
  'contract-changes': 'Alterações contratuais',
  'general-inquiry': 'Consultas gerais',
  cancellation: 'Cancelamento',
  'cadastral-update': 'Alterações cadastrais',
  'appointment-confirmation': 'Confirmação de agendamento',
  // Saúde
  'medical-decisions': 'Decisões médicas',
  'surgical-consent': 'Consentimento cirúrgico',
  'medication-decisions': 'Decisões de medicação',
  'records-access': 'Acesso a prontuário',
  // Educação
  'academic-decisions': 'Decisões acadêmicas',
  'pickup-authorization': 'Autorização de retirada',
  'medical-school': 'Saúde escolar',
  // Jurídico
  'court-filing': 'Peticionamento judicial',
  negotiation: 'Negociação',
  'document-receipt': 'Recebimento de documentos',
  'plea-bargain': 'Acordo / delação',
  // Comercial
  signing: 'Assinatura',
  'price-negotiation': 'Negociação de preço',
  'procurement-decisions': 'Decisões de compra',
};

export interface ScopeGroup {
  label: string;
  scopes: readonly BondAuthorizationScope[];
}

export const SCOPE_GROUPS: readonly ScopeGroup[] = [
  {
    label: 'Comum',
    scopes: [
      'financial',
      'technical-support',
      'contract-changes',
      'general-inquiry',
      'cancellation',
      'cadastral-update',
      'appointment-confirmation',
    ],
  },
  {
    label: 'Saúde',
    scopes: ['medical-decisions', 'surgical-consent', 'medication-decisions', 'records-access'],
  },
  { label: 'Educação', scopes: ['academic-decisions', 'pickup-authorization', 'medical-school'] },
  {
    label: 'Jurídico',
    scopes: ['court-filing', 'negotiation', 'document-receipt', 'plea-bargain'],
  },
  { label: 'Comercial', scopes: ['signing', 'price-negotiation', 'procurement-decisions'] },
];

// Exaustividade: todo escopo do enum aparece em exatamente um grupo. Falha em
// build se o enum crescer e o grupo não for atualizado (evita escopo órfão no form).
const GROUPED = new Set(SCOPE_GROUPS.flatMap((g) => g.scopes));
const MISSING_FROM_GROUPS = BOND_AUTHORIZATION_SCOPES.filter((s) => !GROUPED.has(s));
if (MISSING_FROM_GROUPS.length > 0) {
  throw new Error(
    `SCOPE_GROUPS desatualizado — escopos sem grupo: ${MISSING_FROM_GROUPS.join(', ')}`,
  );
}

export const STATUS_LABELS: Record<AuthorizedContactStatus, string> = {
  active: 'Ativo',
  revoked: 'Revogado',
  expired: 'Expirado',
};

/** Rótulo de um escopo; fallback para o código cru (forward-compat se o tenant
 * declarar escopo novo antes do frontend ser atualizado). */
export function scopeLabel(scope: string): string {
  return SCOPE_LABELS[scope as BondAuthorizationScope] ?? scope;
}

export function statusLabel(status: string): string {
  return STATUS_LABELS[status as AuthorizedContactStatus] ?? status;
}

/** Data ISO → dd/mm/aaaa; null/ inválida → '—'. */
export function formatDate(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR', { dateStyle: 'medium' });
}
